import React, { useState, useEffect, useMemo } from "react";
import { isValidUUID } from "../../utils/validation";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Typography,
  Box,
  Checkbox,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import ConfirmationModal from "../modals/ConfirmationModal";
import BulkAssignUsersModal from "../modals/BulkAssignUsersModal";
import BulkPaymentTermsModal from "../modals/BulkPaymentTermsModal";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";
import PaymentsIcon from "@mui/icons-material/Payments";
import CloseIcon from "@mui/icons-material/Close";
import { useBulkDeleteCompaniesMutation } from "../../store/apis/companyApi";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  Company,
  CompanyWithBillings,
  IntegrationType,
} from "../../types/CompanyTypes";
import { integrationColors } from "../../utils/colorUtils";

interface CompanyTableProps {
  companiesData: (Company | CompanyWithBillings)[];
  maxHeight?: number | string;
  stickyHeader?: boolean;
  onView?: (company: Company | CompanyWithBillings) => void;
  isAdmin?: boolean;
  canDeleteCompany?: boolean;
  canBulkManage?: boolean;
}

const CompanyTable: React.FC<CompanyTableProps> = ({
  companiesData,
  maxHeight,
  stickyHeader = false,
  onView,
  isAdmin = false,
  canDeleteCompany = false,
  canBulkManage = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(
    new Set()
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  // Client-side search with ~250ms debounce
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput), 250);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const filteredCompanies = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return companiesData;
    return companiesData.filter((c) =>
      [c.nameCommercial, c.nameFiscal, c.nif].some((field) =>
        (field ?? "").toLowerCase().includes(q)
      )
    );
  }, [companiesData, debouncedSearch]);

  // Valid (UUID) ids among currently visible/filtered rows
  const selectableIds = useMemo(
    () => filteredCompanies.map((c) => c.id).filter(isValidUUID),
    [filteredCompanies]
  );
  const selectedVisibleCount = selectableIds.filter((id) =>
    selectedCompanies.has(id)
  ).length;

  // Selection is available to anyone who can run a bulk action
  const canSelect = canDeleteCompany || canBulkManage;

  // Number of columns rendered (for empty-state colSpan)
  const columnCount =
    1 /* name */ +
    1 /* fiscal */ +
    1 /* nif */ +
    (canSelect ? 1 : 0) +
    (isAdmin ? 1 : 0);
  const [
    bulkDeleteCompanies,
    { isLoading: isDeleting, data: deletedCompanyIds, error: deleteError },
  ] = useBulkDeleteCompaniesMutation();
  const dispatch = useDispatch();

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Select only the currently visible (filtered) rows with valid UUIDs
      setSelectedCompanies(new Set(selectableIds));
    } else {
      setSelectedCompanies(new Set());
    }
  };

  const handleSelectCompany = (id: string) => {
    // Validate UUID format before adding
    if (!isValidUUID(id)) {
      console.error("Invalid UUID format:", id);
      return;
    }
    const newSelected = new Set(selectedCompanies);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedCompanies(newSelected);
  };

  useEffect(() => {
    if (deletedCompanyIds) {
      setSelectedCompanies(new Set());
      dispatch(
        setToastMessage({
          message: t("company.notifications.bulkDeleteSuccess"),
          severity: "success",
        })
      );
      setIsDeleteModalOpen(false);
    }
  }, [deletedCompanyIds, dispatch, t]);

  useEffect(() => {
    if (deleteError) {
      dispatch(
        setToastMessage({
          message: t("company.notifications.bulkDeleteError"),
          severity: "error",
        })
      );
      setIsDeleteModalOpen(false);
    }
  }, [deleteError, dispatch, t]);

  const handleBulkDelete = () => {
    const ids = Array.from(selectedCompanies);
    if (ids.length === 0) {
      dispatch(
        setToastMessage({
          message: t("company.notifications.bulkDeleteError"),
          severity: "error",
        })
      );
      return;
    }
    bulkDeleteCompanies({ ids });
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1.5,
        }}
      >
        <TextField
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t("company.search.placeholder")}
          sx={{ flexGrow: 1, maxWidth: { sm: 420 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="pSmall" color={theme.palette.neutral["600"]}>
          {t("company.search.results", { count: filteredCompanies.length })}
        </Typography>
      </Box>
      {(canBulkManage || canDeleteCompany) && selectedCompanies.size > 0 && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            backgroundColor: theme.palette.background.paper,
            borderRadius: "12px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
          }}
        >
          <Typography variant="pMedium" sx={{ fontWeight: 700 }}>
            {t("company.bulkBar.selected", { count: selectedCompanies.size })}
          </Typography>
          <Button
            size="small"
            startIcon={<CloseIcon />}
            onClick={() => setSelectedCompanies(new Set())}
            sx={{ color: theme.palette.neutral["600"] }}
          >
            {t("company.bulkBar.clear")}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {canBulkManage && (
            <>
              <Button
                variant="outlined"
                startIcon={<PeopleIcon />}
                onClick={() => setIsAssignModalOpen(true)}
              >
                {t("company.bulkBar.assignUsers")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PaymentsIcon />}
                onClick={() => setIsPaymentModalOpen(true)}
              >
                {t("company.bulkBar.paymentTerms")}
              </Button>
            </>
          )}
          {canDeleteCompany && (
            <Button
              variant="contained"
              color="error"
              onClick={handleOpenDeleteModal}
              startIcon={
                isDeleting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DeleteIcon />
                )
              }
              sx={{ color: "white" }}
              disabled={isDeleting}
            >
              {isDeleting
                ? t("common.deleting")
                : `${t("common.delete")} (${selectedCompanies.size})`}
            </Button>
          )}
        </Box>
      )}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: maxHeight,
          boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.04)",
          borderRadius: "12px",
          mb: 2,
        }}
      >
        <Table stickyHeader={stickyHeader} aria-label="company table">
          <TableHead>
            <TableRow>
              {canSelect && (
                <TableCell
                  padding="checkbox"
                  sx={{ backgroundColor: theme.palette.background.paper }}
                >
                  <Checkbox
                    indeterminate={
                      selectedVisibleCount > 0 &&
                      selectedVisibleCount < selectableIds.length
                    }
                    checked={
                      selectableIds.length > 0 &&
                      selectedVisibleCount === selectableIds.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              <TableCell
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  fontWeight: 700,
                }}
              >
                {t("company.table.nameCommercial")}
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  fontWeight: 700,
                }}
              >
                {t("company.table.nameFiscal")}
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  fontWeight: 700,
                }}
              >
                {t("company.table.nif")}
              </TableCell>
              {isAdmin && (
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    fontWeight: 700,
                  }}
                >
                  {t("company.table.integrations")}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCompanies.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  sx={{ textAlign: "center", py: 6 }}
                >
                  <Typography
                    variant="pSmall"
                    color={theme.palette.neutral["600"]}
                  >
                    {t("company.search.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredCompanies.map((company, index) => (
              <TableRow
                key={company.id}
                onClick={() => onView && onView(company)}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  cursor: onView ? "pointer" : "default",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  backgroundColor: selectedCompanies.has(company.id)
                    ? theme.palette.action.selected
                    : index % 2 === 1
                      ? theme.palette.neutral["100"]
                      : "inherit",
                }}
              >
                {canSelect && (
                  <TableCell
                    padding="checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selectedCompanies.has(company.id)}
                      onChange={() => handleSelectCompany(company.id)}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {company.imageBase64 && (
                      <Box
                        component="img"
                        src={`data:image/png;base64,${company.imageBase64}`}
                        alt={company.nameCommercial}
                        sx={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: 9999,
                        }}
                      />
                    )}
                    {company.nameCommercial}
                  </Box>
                </TableCell>
                <TableCell>{company.nameFiscal}</TableCell>
                <TableCell>{company.nif}</TableCell>
                {isAdmin && (
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {company.dv360AdvertiserIds &&
                        company.dv360AdvertiserIds.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              backgroundColor:
                                integrationColors[IntegrationType.DV360]
                                  .background,
                              color:
                                integrationColors[IntegrationType.DV360].text,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {IntegrationType.DV360}
                          </Typography>
                        )}
                      {company.taboolaAccountIds &&
                        company.taboolaAccountIds.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              backgroundColor:
                                integrationColors[IntegrationType.TABOOLA]
                                  .background,
                              color:
                                integrationColors[IntegrationType.TABOOLA].text,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {IntegrationType.TABOOLA}
                          </Typography>
                        )}
                      {company.outbrainAccountIds &&
                        company.outbrainAccountIds.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              backgroundColor:
                                integrationColors[IntegrationType.OUTBRAIN]
                                  .background,
                              color:
                                integrationColors[IntegrationType.OUTBRAIN]
                                  .text,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {IntegrationType.OUTBRAIN}
                          </Typography>
                        )}
                      {company.spotifyAdAccountIds &&
                        company.spotifyAdAccountIds.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              backgroundColor:
                                integrationColors[IntegrationType.SPOTIFY]
                                  .background,
                              color:
                                integrationColors[IntegrationType.SPOTIFY].text,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {IntegrationType.SPOTIFY}
                          </Typography>
                        )}
                      {company.hivestackAdvertiserIds &&
                        company.hivestackAdvertiserIds.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              backgroundColor:
                                integrationColors[IntegrationType.HIVESTACK]
                                  .background,
                              color:
                                integrationColors[IntegrationType.HIVESTACK]
                                  .text,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {IntegrationType.HIVESTACK}
                          </Typography>
                        )}
                      {company.amazonAdvertiserIds &&
                        company.amazonAdvertiserIds.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              backgroundColor:
                                integrationColors[IntegrationType.AMAZON]
                                  .background,
                              color:
                                integrationColors[IntegrationType.AMAZON].text,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {IntegrationType.AMAZON}
                          </Typography>
                        )}
                      {company.metaAdAccountIds &&
                        company.metaAdAccountIds.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              backgroundColor:
                                integrationColors[IntegrationType.META]
                                  .background,
                              color:
                                integrationColors[IntegrationType.META].text,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {IntegrationType.META}
                          </Typography>
                        )}
                      {company.tiktokAdvertiserIds &&
                        company.tiktokAdvertiserIds.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              backgroundColor:
                                integrationColors[IntegrationType.TIKTOK]
                                  .background,
                              color:
                                integrationColors[IntegrationType.TIKTOK].text,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {IntegrationType.TIKTOK}
                          </Typography>
                        )}
                      {company.googleAdsCustomerIds &&
                        company.googleAdsCustomerIds.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              backgroundColor:
                                integrationColors[IntegrationType.GOOGLE_ADS]
                                  .background,
                              color:
                                integrationColors[IntegrationType.GOOGLE_ADS]
                                  .text,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {IntegrationType.GOOGLE_ADS}
                          </Typography>
                        )}
                      {company.redditAdAccountIds &&
                        company.redditAdAccountIds.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              backgroundColor:
                                integrationColors[IntegrationType.REDDIT]
                                  .background,
                              color:
                                integrationColors[IntegrationType.REDDIT].text,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {IntegrationType.REDDIT}
                          </Typography>
                        )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmationModal
        open={isDeleteModalOpen}
        title={t("company.deleteConfirmation.title")}
        message={t("company.deleteConfirmation.message", {
          count: selectedCompanies.size,
        })}
        onConfirm={handleBulkDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleting}
      />
      <BulkAssignUsersModal
        open={isAssignModalOpen}
        companyIds={Array.from(selectedCompanies)}
        onClose={() => setIsAssignModalOpen(false)}
        onSuccess={() => setSelectedCompanies(new Set())}
      />
      <BulkPaymentTermsModal
        open={isPaymentModalOpen}
        companyIds={Array.from(selectedCompanies)}
        onClose={() => setIsPaymentModalOpen(false)}
        onSuccess={() => setSelectedCompanies(new Set())}
      />
    </Box>
  );
};

export default CompanyTable;
